import { Fragment, useEffect, useState } from 'react';
import { uebersicht } from '../services/netzwerk';

/**
 * 🏪 Netzwerk-Übersicht für den Inhaber: je Shop lagernd, länger als 7 Tage, erfasst / ausgegeben /
 * zurückgeschickt im Zeitraum; Shop antippen → Mitarbeiter-Zahlen des Shops. Nur Zahlen, nie Namen von Empfängern.
 */
const ZEITRAEUME = [1, 7, 30];

export default function NetzwerkUebersicht({ t, darkMode, refreshKey = 0, kompakt = false }) {
  const [tage, setTage] = useState(7);
  const [daten, setDaten] = useState(null);
  const [fehler, setFehler] = useState(false);
  const [offen, setOffen] = useState(null); // Shop-ID mit aufgeklappten Mitarbeitern

  useEffect(() => {
    let lebt = true;
    setFehler(false);
    uebersicht(tage).then((d) => { if (lebt) setDaten(d); }).catch(() => { if (lebt) setFehler(true); });
    return () => { lebt = false; };
  }, [tage, refreshKey]);

  const shops = (daten && Array.isArray(daten.shops)) ? daten.shops : [];
  const rand = darkMode ? '#3a404c' : '#e6e8ec';
  const sub = darkMode ? '#aab' : '#667';
  const zelle = { padding: kompakt ? '7px 4px' : '8px 6px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', borderTop: `1px solid ${rand}`, whiteSpace: 'nowrap' };
  const kopf = { padding: '6px', textAlign: 'right', fontSize: 11, fontWeight: 600, color: sub, textTransform: 'uppercase', letterSpacing: 0.5, whiteSpace: 'nowrap' };
  const summe = (f) => shops.reduce((n, s) => n + (s[f] || 0), 0);

  return (
    <div data-testid="network-overview" style={kompakt ? {} : { background: darkMode ? '#2d323c' : '#ffffff', borderRadius: 16, padding: 24, marginBottom: 24,
      boxShadow: darkMode ? '0 4px 20px rgba(0,0,0,0.3)' : '0 4px 20px rgba(0,0,0,0.08)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
        {!kompakt && <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 10 }}><span>🏪</span> {daten && daten.name ? daten.name : t('network.overviewTitle')}</h3>}
        <div style={{ display: 'flex', gap: 4 }}>
          {ZEITRAEUME.map((n) => (
            <button key={n} type="button" data-testid={`network-days-${n}`} onClick={() => setTage(n)}
              style={{ padding: '5px 10px', borderRadius: 999, fontSize: 13, cursor: 'pointer', fontWeight: tage === n ? 700 : 400,
                border: `1px solid ${tage === n ? '#1976d2' : rand}`, background: tage === n ? '#1976d2' : 'transparent', color: tage === n ? '#fff' : 'inherit' }}>
              {n === 1 ? t('staffs.today') : t('staffs.days', { n })}
            </button>
          ))}
        </div>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: kompakt ? 13 : 14 }}>
          <thead>
            <tr>
              <th style={{ ...kopf, textAlign: 'left' }}>{t('network.colShop')}</th>
              <th style={kopf}>{t('network.colStored')}</th>
              <th style={kopf} title={t('network.overdueHint', { n: (daten && daten.ueberfaelligTage) || 7 })}>{t('network.colOverdue', { n: (daten && daten.ueberfaelligTage) || 7 })}</th>
              <th style={kopf}>{t('staffs.colCaptured')}</th>
              <th style={kopf}>{t('staffs.colIssued')}</th>
              <th style={kopf}>{t('staffs.colReturned')}</th>
            </tr>
          </thead>
          <tbody>
            {shops.map((s) => (
              <Fragment key={s.id}>
                <tr data-testid="network-shop" onClick={() => setOffen(offen === s.id ? null : s.id)} style={{ cursor: s.mitarbeiter.length ? 'pointer' : 'default' }}>
                  <td style={{ ...zelle, textAlign: 'left', fontWeight: 600, maxWidth: kompakt ? 120 : 220, overflow: 'hidden', textOverflow: 'ellipsis' }}
                    title={s.shopName}>
                    {s.mitarbeiter.length ? (offen === s.id ? '▾ ' : '▸ ') : ''}{s.shopName}{s.inhaber && <span style={{ color: sub, fontWeight: 400 }}> · {t('network.you')}</span>}
                  </td>
                  <td style={zelle}>{s.lagernd}</td>
                  <td style={{ ...zelle, color: s.ueberfaellig ? '#d9822b' : 'inherit', fontWeight: s.ueberfaellig ? 700 : 400 }}>{s.ueberfaellig}</td>
                  <td style={zelle}>{s.erfasst}</td>
                  <td style={zelle}>{s.ausgegeben}</td>
                  <td style={zelle}>{s.zurueckgeschickt}</td>
                </tr>
                {offen === s.id && s.mitarbeiter.map((m) => (
                  <tr key={`${s.id}-${m.name}`} data-testid="network-staff" style={{ color: sub, fontSize: 13 }}>
                    <td style={{ ...zelle, textAlign: 'left', paddingLeft: 22, borderTop: 'none', maxWidth: kompakt ? 120 : 220, overflow: 'hidden', textOverflow: 'ellipsis' }} title={m.name}>👷 {m.name}{!m.aktiv ? ` · ${t('staffs.removed')}` : ''}</td>
                    <td style={{ ...zelle, borderTop: 'none' }} /><td style={{ ...zelle, borderTop: 'none' }} />
                    <td style={{ ...zelle, borderTop: 'none' }}>{m.erfasst}</td>
                    <td style={{ ...zelle, borderTop: 'none' }}>{m.ausgegeben}</td>
                    <td style={{ ...zelle, borderTop: 'none' }}>{m.zurueckgeschickt}</td>
                  </tr>
                ))}
              </Fragment>
            ))}
            {shops.length > 1 && (
              <tr data-testid="network-total" style={{ fontWeight: 700 }}>
                <td style={{ ...zelle, textAlign: 'left', borderTop: `2px solid ${rand}` }}>{t('network.total')}</td>
                <td style={{ ...zelle, borderTop: `2px solid ${rand}` }}>{summe('lagernd')}</td>
                <td style={{ ...zelle, borderTop: `2px solid ${rand}` }}>{summe('ueberfaellig')}</td>
                <td style={{ ...zelle, borderTop: `2px solid ${rand}` }}>{summe('erfasst')}</td>
                <td style={{ ...zelle, borderTop: `2px solid ${rand}` }}>{summe('ausgegeben')}</td>
                <td style={{ ...zelle, borderTop: `2px solid ${rand}` }}>{summe('zurueckgeschickt')}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {!daten && !fehler && <div style={{ fontSize: 13, color: sub, marginTop: 8 }}>⏳</div>}
      {fehler && <div style={{ fontSize: 13, color: '#e5484d', marginTop: 8 }}>⚠️ {t('staffs.offline')}</div>}
      <div style={{ fontSize: 12, color: sub, marginTop: 10, lineHeight: 1.45 }}>{t('network.privacyNote')}</div>
    </div>
  );
}
